// Auth0 Authentication API Service
// This service handles direct username/password authentication without redirects
//
// IMPORTANT: To use this service, you must enable the following in your Auth0 dashboard:
// 1. Go to Applications > Your App > Settings
// 2. Enable "Allow Offline Access" (for refresh tokens)
// 3. Go to Applications > Your App > Advanced Settings > Grant Types
// 4. Enable "Password" grant type (Resource Owner Password Grant)
// 5. Make sure your database connection name matches the 'connection' property below

const getAuth0Domain = () => {
  return import.meta.env.VITE_AUTH0_DOMAIN || 'dev-ozzl3buxs6xil1us.us.auth0.com';
};

const getAuth0ClientId = () => {
  return import.meta.env.VITE_AUTH0_CLIENT_ID || '7IRgpKS9Wmux8FswWV8o8gElzybIpZHy';
};

interface Auth0SignUpResponse {
  _id: string;
  email: string;
  email_verified: boolean;
}

interface Auth0TokenResponse {
  access_token: string;
  id_token: string;
  token_type: string;
  expires_in: number;
}

interface Auth0UserInfo {
  sub: string;
  email: string;
  email_verified: boolean;
  name?: string;
  nickname?: string;
  picture?: string;
  [key: string]: any;
}

export class Auth0Service {
  private domain: string;
  private clientId: string;
  private connection: string = 'Username-Password-Authentication'; // Default Auth0 database connection

  constructor() {
    this.domain = getAuth0Domain();
    this.clientId = getAuth0ClientId();
  }

  /**
   * Sign up a new user with Auth0
   */
  async signUp(email: string, password: string, fullName?: string): Promise<Auth0SignUpResponse> {
    const response = await fetch(`https://${this.domain}/dbconnections/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        client_id: this.clientId,
        email,
        password,
        connection: this.connection,
        name: fullName,
        user_metadata: {
          full_name: fullName,
        },
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Sign up failed' }));
      throw new Error(error.description || error.message || 'Sign up failed');
    }

    return await response.json();
  }

  /**
   * Sign in with username/password using Resource Owner Password Grant
   */
  async signIn(email: string, password: string): Promise<Auth0TokenResponse> {
    const response = await fetch(`https://${this.domain}/oauth/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        grant_type: 'password',
        client_id: this.clientId,
        username: email,
        password,
        connection: this.connection,
        scope: 'openid profile email',
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Sign in failed' }));
      throw new Error(error.error_description || error.error || 'Sign in failed');
    }

    return await response.json();
  }

  /**
   * Get user information from Auth0 using the access token
   */
  async getUserInfo(accessToken: string): Promise<Auth0UserInfo> {
    const response = await fetch(`https://${this.domain}/userinfo`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to get user info' }));
      throw new Error(error.message || 'Failed to get user info');
    }

    return await response.json();
  }

  /**
   * Refresh an access token using refresh token (if available)
   */
  async refreshToken(refreshToken: string): Promise<Auth0TokenResponse> {
    const response = await fetch(`https://${this.domain}/oauth/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        grant_type: 'refresh_token',
        client_id: this.clientId,
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Token refresh failed' }));
      throw new Error(error.error_description || error.error || 'Token refresh failed');
    }

    return await response.json();
  }
}

export const auth0Service = new Auth0Service();

